/**
 * `ContextIndicator` — the always-on "you are here" scope bar.
 *
 * CCM Handoff #2 §2.1/§2.2/§2.3 (2026-06-28): HOISTED from inside
 * `CaseWorkspaceLayout` up to the App shell (`AppShell` in App.tsx) so
 * it renders on EVERY route — including `/home` and `/chat` — and the
 * lawyer can always tell which scope the concierge is in. Before this,
 * the indicator lived only inside the case layout, so `/home` + `/chat`
 * showed nothing while a case could still be highlighted in nav (the
 * "stale-selection trap"). The bar now surfaces:
 *
 *   - the active scope: a **client/matter/case breadcrumb** (IDs today)
 *     OR an explicit **"Global / General"** state when nothing is
 *     selected (the per-user org scope, not a case);
 *   - a **stale-selection cue** when a case is selected in state but the
 *     current route is the global thread (/home, /chat) — i.e. the chat
 *     is silently global while a case is highlighted;
 *   - a **"Resume <case> →"** affordance from the global thread that
 *     re-selects the remembered case + navigates into its workspace;
 *   - (a **"Switch"** affordance opened a deterministic case-name picker;
 *     the button went with F1/D-2 and `ContextSwitcher` itself was DELETED
 *     2026-08-06 — typed case search lives in the COMMAND PALETTE, Ctrl+K);
 *   - the existing info popover summarising what the AI has in context
 *     (active scope, open file, text selection, the real session key).
 *
 * Pure render — no network calls; all state comes from stores +
 * `useLocation` for route awareness. The case-title for "Resume" is
 * read from the localStorage-backed recent-work list (`useRecentWork`),
 * so there's no extra round trip.
 *
 * Switching is the L1 session-key→transcript change, client-
 * authoritative — the SPA builds the key (`useDerivedChatSessionKey`),
 * the runtime only parses it. Never an LLM decision.
 *
 * See: ai-docs/handoffs/ccm-lexide-clarity-slice-handoff-2026-06-28.md,
 *      ai-docs/lexiun-concierge-context-model-guide.md §3.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  ChevronRight,
  FolderOpen,
  Globe,
  Repeat,
  Scissors,
  X,
} from "lucide-react";
import {
  useActiveContextStore,
  type ActiveSelection,
} from "@/stores/useActiveContextStore";
import { useCaseWorkspaceStore } from "@/stores/useCaseWorkspaceStore";
import { pickLabel } from "@/lib/case-brief";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";
import { useDocSelectionStore } from "@/stores/useDocSelectionStore";
import { useDerivedChatSessionKey } from "@/hooks/useDerivedChatSessionKey";
import { useRecentWork } from "@/hooks/useRecentWork";
import { useScopeNames } from "@/hooks/useScopeNames";
import { cn } from "@/lib/utils";

/** Routes where the concierge chat runs in the GLOBAL (per-user org)
 *  scope regardless of any case highlighted in nav. `useDerivedChat
 *  SessionKey` still keys those surfaces to `agent:main:lx:global`
 *  there — the indicator reflects that truth. */
function isGlobalThreadRoute(pathname: string): boolean {
  return pathname === "/home" || pathname.startsWith("/home/") || pathname === "/chat";
}

export function ContextIndicator() {
  const { t, i18n } = useTranslation();
  const selection = useActiveContextStore((s) => s.selection);
  const selectCase = useActiveContextStore((s) => s.selectCase);
  const activeFile = useWorkspaceFileStore((s) => s.activeFile);
  const docSelection = useDocSelectionStore((s) => s.selection);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Resolved scope names for the chain + the diagnostic popover (B-1/B-4).
  const scopeNames = useScopeNames(selection);

  // The case-title for the "Resume" affordance. Read from the
  // localStorage recent-work list (no round trip).
  //
  // B-5: the old `?? selectedCaseId` made this button read "Resume
  // case-01K… →" whenever the title had not been recorded. It now falls through
  // to the resolver, which ends at the matter-typed generic label and never at
  // an id — consistent with B-3, and it keeps a working affordance rather than
  // hiding the chip over a cosmetic gap.
  const recentCases = useRecentWork({ kinds: ["case"], limit: 10 });
  const selectedCaseId = selection?.caseId ?? null;
  const recentCaseEntry = selectedCaseId
    ? recentCases.find((e) => e.id === selectedCaseId)
    : undefined;
  const resumeTitle = recentCaseEntry?.label ?? scopeNames.case?.label ?? "";

  // The chat is GLOBAL while on /home or /chat — even if a case is
  // still selected in state. That mismatch is the stale-selection
  // trap; surface it explicitly.
  const onGlobalThread = isGlobalThreadRoute(location.pathname);
  // L11: only a case with a full parent chain is RESUMABLE (handleResume builds the
  // 4-level case key from the parents). An unassigned case (null parents) can't be
  // resumed, so don't surface the stale/Resume cue for it (it would be a dead button).
  const isStale =
    onGlobalThread &&
    selection?.caseId != null &&
    selection?.clientId != null &&
    selection?.matterId != null;
  // What scope is the chat ACTUALLY in right now? On a global thread
  // route it's always global; otherwise it follows the selection.
  const effectiveScopeIsGlobal = onGlobalThread || selection == null;

  /* D-3 — the case HEADLINE takes over identity when it exists.
     Operator ratified the trade (the navigator already shows the chain) with the
     fallback I flagged: **the headline replaces the breadcrumb only WHEN it
     exists.** A focus pane collapses the navigator and a document-less case has
     no headline, so an absolute swap could leave nothing naming the case.

     ⚠ VISUALLY hidden, NOT removed (operator's option 1). Two reasons, and the
     first is not cosmetic: two commits ago the B-1/B-2 "no raw ids in
     user-facing copy" e2e assertion was re-pointed ONTO this breadcrumb, because
     it became the single canonical chain (B-8's whole point). Deleting it from
     the DOM would delete the only surface that PROVES a ratified invariant — and
     the headline cannot carry it, being authored prose with no `data-scope-id`.
     Second: a screen reader keeps the full scope chain, which an authored
     one-liner does not reliably convey.

     ⚠ Guarded on `workspaceCaseId === selection.caseId`, the same guard
     `useScopeNames` uses for the same hazard: the workspace store can still hold
     a PREVIOUS case's data after navigating away, and a stale headline must
     never hide the breadcrumb on a route it does not describe. */
  const workspaceCaseId = useCaseWorkspaceStore((st) => st.caseId);
  const workspaceHeadline = useCaseWorkspaceStore((st) => st.data?.brief?.headline);
  const headlineOwnsIdentity = Boolean(
    selection?.caseId &&
      workspaceCaseId === selection.caseId &&
      pickLabel(workspaceHeadline, i18n.language),
  );

  /* ⛔ The popover's outside-click / Escape / focus-trap effects went with
     it. Three listeners and a focus ref existed to manage a surface that no
     longer opens. */

  function handleResume() {
    if (!selection?.clientId || !selection.matterId || !selection.caseId) return;
    // Re-select (idempotent — keeps the selection) + navigate into the
    // case workspace via the existing long-URL route.
    selectCase(selection.clientId, selection.matterId, selection.caseId);
    navigate(
      `/clients/${selection.clientId}/matters/${selection.matterId}/cases/${selection.caseId}`,
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="context-indicator"
      data-has-selection={selection !== null}
      data-has-file={activeFile !== null}
      data-has-doc-selection={docSelection !== null}
      data-scope={effectiveScopeIsGlobal ? "global" : selection?.scope ?? "global"}
      data-stale-selection={isStale}
      /* Which surface is carrying the case's identity right now. The swap is
         otherwise invisible to a test, and an untestable swap is how a
         regression to "both render" or "neither renders" would ship. */
      data-identity-source={headlineOwnsIdentity && !isStale ? "headline" : "breadcrumb"}
      className="relative rounded-lg"
    >
      <div
        className={cn(
          headlineOwnsIdentity && !isStale
            ? // Takes NO layout space, so the bar collapses rather than leaving
              // an empty padded row — the wasted-line defect this pass has been
              // deleting all session. Still in the DOM, still readable by a
              // screen reader, still assertable by the invariant's spec.
              "sr-only"
            : "flex w-full items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400",
        )}
      >
        {/* Not a button any more: with the popover gone there is nothing to
            open, and a control that does nothing is the dead-affordance lie
            this pass keeps deleting. The testid survives — the breadcrumb is
            still the thing specs locate — but it now names plain text. */}
        <div
          data-testid="context-indicator-trigger"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {effectiveScopeIsGlobal ? (
            <Globe size={12} className="shrink-0 text-slate-400" aria-hidden />
          ) : (
            <FolderOpen size={12} className="shrink-0 text-slate-400" aria-hidden />
          )}
          {effectiveScopeIsGlobal ? (
            <span
              data-testid="context-indicator-global-label"
              className="truncate font-medium text-slate-500 dark:text-slate-400"
            >
              {t("chat.context.globalScope")}
            </span>
          ) : (
            <BreadcrumbSegments selection={selection} />
          )}
          {docSelection && (
            <span
              data-testid="context-indicator-selection-adornment"
              className="flex items-center gap-1 text-slate-600 dark:text-slate-300"
            >
              <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                ·
              </span>
              <Scissors size={10} aria-hidden />
              <span>
                L{docSelection.startLine}-{docSelection.endLine}
              </span>
            </span>
          )}
        </div>

        {/* Stale-selection cue + Resume: on a global-thread route while a
            case is still selected, the chat is silently global. Tell the
            lawyer, and offer a one-tap return into the case workspace. */}
        {isStale && (
          <button
            type="button"
            onClick={handleResume}
            data-testid="context-indicator-resume"
            data-case-id={selectedCaseId ?? undefined}
            title={t("chat.context.staleCue")}
            className="flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50"
          >
            <Repeat size={10} aria-hidden />
            <span className="max-w-[12rem] truncate">
              {t("chat.context.resumeCase", { title: resumeTitle })}
            </span>
            <ArrowRight size={10} aria-hidden />
          </button>
        )}

        {/* ⛔ "Trocar" REMOVED — F1/D-2, operator-ratified 2026-07-29.
            The CASOS navigator IS the switcher: clicking a case switches to
            it, from every route, with the full tree visible. A second
            switcher in the always-on bar bought nothing and cost a control
            on every screen.

            ⚠ VERIFIED, not assumed: typed case search survives in the COMMAND
            PALETTE (Ctrl+K — it already fetches the case list and opens by
            id). This button was `ContextSwitcher`'s LAST entry point, which
            left the component unreachable; the operator called it on
            2026-08-06 and the component + its store are now DELETED. */}
      </div>

      {/* ⛔ THE ⓘ POPOVER IS GONE — operator 2026-07-29.
          It was a DIAGNOSTIC detail surface: the resolved-vs-raw id list, the
          session key, and a duplicate of the stale notice + Resume.

          ⚠ Verified before removing, not assumed: the RECOVERY PATH survives.
          The stale cue and the Resume button live INLINE on the bar above
          (`context-indicator-resume`) and are what a lawyer actually meets —
          the popover's copies were second instances behind a click. Nothing
          about drift detection or recovery is lost here.

          What IS lost is the id/session-key diagnostic. That was a debugging
          affordance, not a lawyer-facing one, and `DerivedSessionKeyProbe`
          still prints a session key for testing. */}
    </div>
  );
}

/**
 * The always-on scope chain (B-1). Renders HUMAN NAMES — never ids.
 *
 * The id still travels, on a `data-*` attribute, which is what `Breadcrumbs.tsx`
 * was really after with its "still diagnosable" rationale: a `data-*` is
 * inspectable and assertable but never reaches copy, a screen reader, or a
 * copy-paste. `font-mono` is gone with the ids — a name is prose.
 */
function BreadcrumbSegments({ selection }: { selection: ActiveSelection | null }) {
  const { t } = useTranslation();
  // Unconditional — `useScopeNames` handles a null selection, and a hook may not
  // sit behind the early return below.
  const names = useScopeNames(selection);
  if (!selection) {
    return (
      <span className="truncate italic text-slate-400 dark:text-slate-500">
        {t("chat.context.breadcrumbEmpty")}
      </span>
    );
  }
  const parts: Array<{
    scope: "client" | "matter" | "case";
    label: string;
    id: string | null;
    isGeneric: boolean;
  }> = [];
  // clientId is null for an unassigned (assignment-pending) case (D-8) —
  // show the "Unassigned" affordance instead of an empty segment. That is a
  // real state, not an unresolved name, so it is not `isGeneric`.
  parts.push({
    scope: "client",
    label: selection.clientId
      ? (names.client?.label ?? t("workspaceTree.unassignedHeading"))
      : t("workspaceTree.unassignedHeading"),
    id: selection.clientId,
    isGeneric: Boolean(selection.clientId) && Boolean(names.client?.isGeneric),
  });
  if (selection.matterId && names.matter)
    parts.push({
      scope: "matter",
      label: names.matter.label,
      id: selection.matterId,
      isGeneric: names.matter.isGeneric,
    });
  if (selection.caseId && names.case)
    parts.push({
      scope: "case",
      label: names.case.label,
      id: selection.caseId,
      isGeneric: names.case.isGeneric,
    });
  return (
    <span className="flex min-w-0 items-center gap-1 truncate">
      {parts.map((p, i) => (
        <span key={p.scope} className="flex items-center gap-1">
          {i > 0 && (
            <ChevronRight
              size={10}
              className="text-slate-300 dark:text-slate-600"
              aria-hidden
            />
          )}
          <span
            data-testid={`context-indicator-breadcrumb-${p.scope}`}
            data-scope-id={p.id ?? undefined}
            data-generic={p.isGeneric ? "true" : undefined}
            className="truncate"
          >
            {p.label}
          </span>
        </span>
      ))}
    </span>
  );
}

/* ⛔ `Section` DELETED with the popover — it laid out that surface's labelled
   blocks and had no other caller. An unused layout helper still carries its
   bugs, and leaving it implies a surface that no longer exists. */

function MutedLine({ children }: { children: ReactNode }) {
  return (
    <div className="text-[11px] italic text-slate-400 dark:text-slate-500">
      {children}
    </div>
  );
}
